import axios from 'axios';
import type { AuthResponse, LoginCredentials, RegisterData, User, ChatRoom, Message } from '../types';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - don't redirect for public endpoints
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Only redirect if we're not already on login/register page
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        // Don't auto-redirect, let the component handle it
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    return response.data;
  },
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
  getMe: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },
};

export const roomsApi = {
  list: async (category?: string): Promise<ChatRoom[]> => {
    const params = category ? { category } : {};
    const response = await api.get<ChatRoom[]>('/rooms', { params });
    return response.data;
  },
  get: async (id: string): Promise<ChatRoom> => {
    const response = await api.get<ChatRoom>(`/rooms/${id}`);
    return response.data;
  },
  create: async (data: { name: string; description?: string }): Promise<ChatRoom> => {
    const response = await api.post<ChatRoom>('/rooms', data);
    return response.data;
  },
};

export const messagesApi = {
  list: async (roomId: string): Promise<Message[]> => {
    const response = await api.get<Message[]>(`/rooms/${roomId}/messages`);
    return response.data;
  },
};

